/**
 * NewsStatsPage.jsx — src/content/Manager/News/NewsStatsPage.jsx
 * Роут: /news/:id/stats   (только head_manager / administrator)
 */
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
    Box, Button, Chip, CircularProgress, IconButton, Input, Option, Select,
    Sheet, Stack, Table, Tooltip, Typography,
} from '@mui/joy';
import { motion } from 'framer-motion';
import {
    Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart,
    Pie, PieChart, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis,
} from 'recharts';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';
import TrackChangesRoundedIcon from '@mui/icons-material/TrackChangesRounded';
import LayersRoundedIcon from '@mui/icons-material/LayersRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import PersonOffRoundedIcon from '@mui/icons-material/PersonOffRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';

import { LayoutContext } from '../../../context';
import {
    APIDownloadNewsStatsCsv, APIGetNewsNotViewed, APIGetNewsStatsDetail, APIGetNewsViewers,
} from '../../../api';

const PALETTE = ['#6366f1', '#8b5cf6', '#0ea5e9', '#16a34a', '#f59e0b', '#ef4444', '#14b8a6', '#ec4899'];

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

const PERIODS = [
    { value: 'all', label: 'За всё время' },
    { value: '7', label: '7 дней' },
    { value: '30', label: '30 дней' },
    { value: '90', label: '90 дней' },
];

const fmtSec = (sec) => {
    const v = Number(sec || 0);
    if (v === 0) return '0 с';
    // Округление вниз превращало 0.4 с в «0 с» — выглядело так,
    // будто человек ничего не смотрел
    if (v < 1) return '<1 с';
    if (v < 60) return `${Math.round(v)} с`;
    return `${Math.floor(v / 60)} мин ${Math.round(v % 60)} с`;
};

const fmtDateTime = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('ru-RU', {
            day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
        });
    } catch { return iso; }
};

const fmtDay = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' }); }
    catch { return iso; }
};

const cardSx = { borderRadius: '18px', border: '1px solid', borderColor: 'divider', p: 2.2, bgcolor: 'background.surface' };


function MetricCard({ icon, value, label, hint, color = '#6366f1' }) {
    return (
        <Sheet sx={{ ...cardSx, p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
                width: 40, height: 40, borderRadius: '12px', flexShrink: 0,
                background: `${color}1a`, color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', lineHeight: 1.1 }}>{value}</Typography>
                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>{label}</Typography>
                {hint && <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.68rem' }}>{hint}</Typography>}
            </Box>
        </Sheet>
    );
}

function ChartCard({ title, subtitle, children, height = 260 }) {
    return (
        <Sheet sx={cardSx}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.95rem' }}>{title}</Typography>
            {subtitle && (
                <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 1.4 }}>{subtitle}</Typography>
            )}
            <Box sx={{ height, mt: subtitle ? 0 : 1.4 }}>
                <ResponsiveContainer width="100%" height="100%">
                    {children}
                </ResponsiveContainer>
            </Box>
        </Sheet>
    );
}

function BreakdownBars({ title, rows = [], emptyText = 'Нет данных' }) {
    const max = Math.max(1, ...rows.map(r => r.count || 0));
    return (
        <Sheet sx={cardSx}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', mb: 1.4 }}>{title}</Typography>
            {rows.length === 0 ? (
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>{emptyText}</Typography>
            ) : (
                <Stack spacing={1.1}>
                    {rows.slice(0, 8).map((row, i) => (
                        <Box key={row.value || row.label}>
                            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
                                <Typography level="body-xs" sx={{ fontWeight: 600 }}>{row.label}</Typography>
                                <Typography level="body-xs" sx={{ fontWeight: 800 }}>
                                    {row.count} <Box component="span" sx={{ color: 'text.tertiary', fontWeight: 500 }}>· {row.percent}%</Box>
                                </Typography>
                            </Stack>
                            <Box sx={{ height: 7, borderRadius: 7, background: 'rgba(148,163,184,0.18)', overflow: 'hidden' }}>
                                <Box sx={{
                                    width: `${(row.count / max) * 100}%`, height: '100%',
                                    background: PALETTE[i % PALETTE.length],
                                    borderRadius: 7, transition: 'width .4s ease',
                                }} />
                            </Box>
                        </Box>
                    ))}
                </Stack>
            )}
        </Sheet>
    );
}


export default function NewsStatsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { tokenEmployee, notify } = useContext(LayoutContext);

    const [period, setPeriod] = useState('all');
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    // Таблица зрителей
    const [viewers, setViewers] = useState({ count: 0, num_pages: 1, results: [], slides_count: 0 });
    const [viewersLoading, setViewersLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [q, setQ] = useState('');
    const [qDebounced, setQDebounced] = useState('');
    const [completedFilter, setCompletedFilter] = useState('');
    const [deviceFilter, setDeviceFilter] = useState('');
    const [ordering, setOrdering] = useState('-started_at');

    // Не увидели
    const [notViewed, setNotViewed] = useState(null);
    const [showNotViewed, setShowNotViewed] = useState(false);

    const periodParams = useCallback(() => {
        if (period === 'all') return {};
        return { date_from: daysAgoISO(Number(period)), date_to: todayISO() };
    }, [period]);

    useEffect(() => {
        const t = setTimeout(() => setQDebounced(q), 400);
        return () => clearTimeout(t);
    }, [q]);

    const loadStats = useCallback(async () => {
        setLoading(true);
        const data = await APIGetNewsStatsDetail(id, tokenEmployee, periodParams());
        if (data?._error) {
            notify('Не удалось загрузить статистику', 'danger');
            navigate('/news');
            return;
        }
        setStats(data);
        setLoading(false);
    }, [id, tokenEmployee, periodParams, notify, navigate]);

    const loadViewers = useCallback(async () => {
        setViewersLoading(true);
        const data = await APIGetNewsViewers(id, tokenEmployee, {
            ...periodParams(),
            page, page_size: 25, q: qDebounced,
            completed: completedFilter, device: deviceFilter, ordering,
        });
        setViewers(data);
        setViewersLoading(false);
    }, [id, tokenEmployee, periodParams, page, qDebounced, completedFilter, deviceFilter, ordering]);

    useEffect(() => { loadStats(); }, [loadStats]);
    useEffect(() => { loadViewers(); }, [loadViewers]);
    useEffect(() => { setPage(1); }, [qDebounced, completedFilter, deviceFilter, ordering, period]);

    const loadNotViewed = async () => {
        if (notViewed) { setShowNotViewed(v => !v); return; }
        const data = await APIGetNewsNotViewed(id, tokenEmployee, { page_size: 100 });
        setNotViewed(data);
        setShowNotViewed(true);
    };

    const downloadCsv = async () => {
        const ok = await APIDownloadNewsStatsCsv(id, tokenEmployee, {
            ...periodParams(), q: qDebounced, completed: completedFilter, device: deviceFilter, ordering,
        });
        notify(ok ? 'Файл выгружен' : 'Не удалось выгрузить CSV', ok ? 'success' : 'danger');
    };

    if (loading || !stats) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    const news = stats.news || {};
    const summary = stats.summary || {};
    const funnel = stats.funnel || [];
    const breakdown = stats.breakdown || {};
    const timeline = stats.timeline || {};

    const funnelData = funnel.map(f => ({
        name: `${f.position}. ${(f.title || '').slice(0, 18)}`,
        viewers: f.viewers,
        percent: f.percent_of_first,
        avg: f.avg_duration_sec,
    }));

    const deviceData = (breakdown.by_device || []).map(d => ({ name: d.label, value: d.count }));

    return (
        <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 }, maxWidth: 1300, mx: 'auto', minWidth: 0 }}>
            {/* Шапка */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                    <Box onClick={() => navigate('/news')} sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                        color: 'text.tertiary', fontSize: '0.82rem', fontWeight: 600, flexShrink: 0,
                        '&:hover': { color: '#6366f1' },
                    }}>
                        <ArrowBackRoundedIcon sx={{ fontSize: 16 }} /> Новости
                    </Box>

                    {news.cover_url && (
                        <Box sx={{
                            width: 46, height: 46, borderRadius: '12px', flexShrink: 0,
                            background: `url(${news.cover_url}) center/cover no-repeat`,
                        }} />
                    )}

                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', lineHeight: 1.15, letterSpacing: '-0.4px' }}>
                            {news.title}
                        </Typography>
                        <Stack direction="row" spacing={0.6} sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.4 }}>
                            <Chip size="sm" variant="soft" sx={{ fontSize: '0.66rem' }}>
                                {news.show_mode === 'always' ? 'Показ: всегда' : `Показ: ${fmtDay(news.show_date)}`}
                            </Chip>
                            <Chip size="sm" variant="soft" sx={{ fontSize: '0.66rem' }}>
                                {news.status === 'published' ? 'Опубликована' : news.status === 'draft' ? 'Черновик' : 'Архив'}
                            </Chip>
                            <Chip size="sm" variant="soft" sx={{ fontSize: '0.66rem' }}>
                                Слайдов: {summary.slides_count}
                            </Chip>
                        </Stack>
                    </Box>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                    <Select value={period} onChange={(e, v) => v && setPeriod(v)} sx={{ minWidth: 150 }}>
                        {PERIODS.map(p => <Option key={p.value} value={p.value}>{p.label}</Option>)}
                    </Select>
                    <Tooltip title="Обновить" size="sm">
                        <IconButton variant="soft" onClick={() => { loadStats(); loadViewers(); }}>
                            <RefreshRoundedIcon />
                        </IconButton>
                    </Tooltip>
                    <Button variant="soft" startDecorator={<EditRoundedIcon />} onClick={() => navigate(`/news/${id}`)}>
                        К редактору
                    </Button>
                </Stack>
            </Stack>

            {/* Метрики */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(5, 1fr)' },
                gap: 1.5, mb: 2,
            }}>
                <MetricCard
                    icon={<GroupRoundedIcon />}
                    value={summary.views_total ?? 0}
                    label="Открыли"
                    hint={`Аудитория: ${summary.audience_total ?? 0}`}
                    color="#0ea5e9"
                />
                <MetricCard
                    icon={<DoneAllRoundedIcon />}
                    value={`${summary.completion_rate ?? 0}%`}
                    label="Досмотрели"
                    hint={
                        `${summary.views_completed ?? 0} чел. · `
                        + `«Ок»: ${summary.views_completed_manual ?? 0}, `
                        + `по таймеру: ${summary.views_completed_auto ?? 0}`
                    }
                    color="#16a34a"
                />
                <MetricCard
                    icon={<TrackChangesRoundedIcon />}
                    value={`${summary.coverage_percent ?? 0}%`}
                    label="Охват аудитории"
                    hint="Кто хотя бы открыл"
                    color="#8b5cf6"
                />
                <MetricCard
                    icon={<LayersRoundedIcon />}
                    value={`${summary.avg_slides_seen ?? 0}`}
                    label="Средняя глубина"
                    hint={`${summary.avg_depth_percent ?? 0}% слайдов`}
                    color="#f59e0b"
                />
                <MetricCard
                    icon={<TimerRoundedIcon />}
                    value={fmtSec(summary.avg_duration_sec)}
                    label="Среднее время"
                    hint={`Открытий на человека: ${summary.avg_open_count ?? 0}`}
                    color="#ec4899"
                />
            </Box>

            {/* Воронка и время на слайде */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.4fr 1fr' }, gap: 2, mb: 2 }}>
                <ChartCard
                    title="Воронка по слайдам"
                    subtitle="Сколько человек дошло до каждого слайда"
                    height={280}
                >
                    <BarChart data={funnelData} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-12} textAnchor="end" height={54} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <RTooltip
                            formatter={(value, name) => (name === 'viewers' ? [value, 'Посмотрели'] : [value, name])}
                            contentStyle={{ borderRadius: 12, border: '1px solid rgba(148,163,184,0.3)', fontSize: 12 }}
                        />
                        <Bar dataKey="viewers" radius={[6, 6, 0, 0]} maxBarSize={54}>
                            {funnelData.map((entry, i) => (
                                <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                            ))}
                        </Bar>
                    </BarChart>
                </ChartCard>

                <ChartCard
                    title="Среднее время на слайде"
                    subtitle="Секунды — где задерживаются, а где пролистывают"
                    height={280}
                >
                    <BarChart data={funnelData} layout="vertical" margin={{ top: 5, right: 16, left: 6, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={104} />
                        <RTooltip
                            formatter={(value) => [`${value} с`, 'Среднее время']}
                            contentStyle={{ borderRadius: 12, border: '1px solid rgba(148,163,184,0.3)', fontSize: 12 }}
                        />
                        <Bar dataKey="avg" fill="#8b5cf6" radius={[0, 6, 6, 0]} maxBarSize={22} />
                    </BarChart>
                </ChartCard>
            </Box>

            {/* Отвал по слайдам — текстом, читается быстрее графика */}
            <Sheet sx={{ ...cardSx, mb: 2 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', mb: 1.4 }}>Отвал между слайдами</Typography>
                <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
                    {funnel.map((f, i) => (
                        <Sheet key={f.slide_id} sx={{
                            flex: '0 0 168px', p: 1.4, borderRadius: '14px',
                            border: '1px solid', borderColor: 'divider',
                        }}>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 700, mb: 0.4 }}>
                                Слайд {f.position}
                            </Typography>
                            <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', lineHeight: 1.1 }}>{f.viewers}</Typography>
                            <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 0.6 }}>
                                {f.percent_of_first}% от первого
                            </Typography>
                            {i > 0 && (
                                <Chip
                                    size="sm"
                                    variant="soft"
                                    color={f.drop_from_prev > 0 ? 'danger' : 'success'}
                                    sx={{ fontSize: '0.66rem' }}
                                >
                                    {f.drop_from_prev > 0 ? `−${f.drop_from_prev} (${f.drop_from_prev_percent}%)` : 'без отвала'}
                                </Chip>
                            )}
                        </Sheet>
                    ))}
                </Stack>
            </Sheet>

            {/* Графики во времени */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <ChartCard title="Динамика по дням" subtitle="Открытия и досмотры">
                    <LineChart data={(timeline.by_date || []).map(d => ({ ...d, day: fmtDay(d.date) }))} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" vertical={false} />
                        <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <RTooltip contentStyle={{ borderRadius: 12, border: '1px solid rgba(148,163,184,0.3)', fontSize: 12 }} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Line type="monotone" dataKey="opens" name="Открыли" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="completes" name="Досмотрели" stroke="#16a34a" strokeWidth={2.5} dot={{ r: 3 }} />
                    </LineChart>
                </ChartCard>

                <ChartCard title="Когда заходят" subtitle="Распределение открытий по часам">
                    <AreaChart data={timeline.by_hour || []} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                        <defs>
                            <linearGradient id="newsHourFill" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#6366f1" stopOpacity={0.5} />
                                <stop offset="100%" stopColor="#6366f1" stopOpacity={0.04} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" vertical={false} />
                        <XAxis dataKey="hour" tick={{ fontSize: 11 }} tickFormatter={(h) => `${h}:00`} interval={2} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <RTooltip
                            labelFormatter={(h) => `${h}:00 — ${h}:59`}
                            formatter={(v) => [v, 'Открытий']}
                            contentStyle={{ borderRadius: 12, border: '1px solid rgba(148,163,184,0.3)', fontSize: 12 }}
                        />
                        <Area type="monotone" dataKey="count" stroke="#6366f1" strokeWidth={2} fill="url(#newsHourFill)" />
                    </AreaChart>
                </ChartCard>
            </Box>

            {/* Разрезы */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
                <BreakdownBars title="По должностям" rows={breakdown.by_position} />
                <BreakdownBars title="По ролям" rows={breakdown.by_role} />
                <BreakdownBars title="По городам" rows={breakdown.by_city} />

                <Sheet sx={cardSx}>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', mb: 1 }}>Устройства</Typography>
                    {deviceData.length === 0 ? (
                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Нет данных</Typography>
                    ) : (
                        <Box sx={{ height: 190 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={deviceData}
                                        dataKey="value"
                                        nameKey="name"
                                        innerRadius={44}
                                        outerRadius={72}
                                        paddingAngle={3}
                                    >
                                        {deviceData.map((entry, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                                    </Pie>
                                    <RTooltip contentStyle={{ borderRadius: 12, border: '1px solid rgba(148,163,184,0.3)', fontSize: 12 }} />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </Box>
                    )}
                </Sheet>
            </Box>

            {/* Таблица зрителей */}
            <Sheet sx={{ ...cardSx, p: 0, mb: 2, overflow: 'hidden' }}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                    <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', flex: 1 }}>
                        Зрители ({viewers.count})
                    </Typography>
                    <Input
                        size="sm"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="ФИО или телефон"
                        startDecorator={<SearchRoundedIcon sx={{ fontSize: 17 }} />}
                        sx={{ minWidth: 190 }}
                    />
                    <Select size="sm" value={completedFilter} onChange={(e, v) => setCompletedFilter(v ?? '')} sx={{ minWidth: 150 }}>
                        <Option value="">Все</Option>
                        <Option value="1">Досмотрели</Option>
                        <Option value="0">Не досмотрели</Option>
                    </Select>
                    <Select size="sm" value={deviceFilter} onChange={(e, v) => setDeviceFilter(v ?? '')} sx={{ minWidth: 140 }}>
                        <Option value="">Все устройства</Option>
                        <Option value="mobile">Мобильные</Option>
                        <Option value="desktop">ПК</Option>
                    </Select>
                    <Select size="sm" value={ordering} onChange={(e, v) => v && setOrdering(v)} sx={{ minWidth: 170 }}>
                        <Option value="-started_at">Сначала новые</Option>
                        <Option value="started_at">Сначала старые</Option>
                        <Option value="-slides_seen">По глубине</Option>
                        <Option value="-total_duration_ms">По времени</Option>
                        <Option value="-open_count">По числу открытий</Option>
                    </Select>
                    <Button size="sm" variant="soft" startDecorator={<DownloadRoundedIcon />} onClick={downloadCsv}>
                        CSV
                    </Button>
                </Stack>

                {viewersLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size="sm" /></Box>
                ) : viewers.results.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>Никто не подходит под фильтры</Typography>
                    </Box>
                ) : (
                    <Box sx={{ overflow: 'auto' }}>
                        <Table
                            hoverRow
                            sx={{
                                '--TableCell-headBackground': 'rgba(99,102,241,0.05)',
                                '& thead th': { fontWeight: 800, fontSize: '0.73rem', whiteSpace: 'nowrap' },
                                '& tbody td': { fontSize: '0.82rem' },
                                minWidth: 900,
                            }}
                        >
                            <thead>
                                <tr>
                                    <th style={{ width: '24%' }}>Сотрудник</th>
                                    <th style={{ width: 130 }}>Должность</th>
                                    <th style={{ width: 120 }}>Город</th>
                                    <th style={{ width: 130 }}>Открыл</th>
                                    <th style={{ width: 130 }}>Завершил</th>
                                    <th style={{ width: 110 }}>Глубина</th>
                                    <th style={{ width: 100 }}>Время</th>
                                    <th style={{ width: 90 }}>Открытий</th>
                                    <th style={{ width: 100 }}>Устройство</th>
                                </tr>
                            </thead>
                            <tbody>
                                {viewers.results.map(v => (
                                    <tr key={v.id}>
                                        <td>
                                            <Stack spacing={0.2}>
                                                <Typography sx={{ fontWeight: 700, fontSize: '0.83rem' }}>{v.full_name}</Typography>
                                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>{v.phone || '—'}</Typography>
                                            </Stack>
                                        </td>
                                        <td>
                                            <Stack direction="row" spacing={0.4} sx={{ flexWrap: 'wrap', gap: 0.3 }}>
                                                {(v.positions || []).length === 0
                                                    ? <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>—</Typography>
                                                    : v.positions.map(p => (
                                                        <Chip key={p} size="sm" variant="soft" sx={{ fontSize: '0.62rem' }}>{p}</Chip>
                                                    ))}
                                            </Stack>
                                        </td>
                                        <td>{v.city || '—'}</td>
                                        <td>{fmtDateTime(v.started_at)}</td>
                                        <td>
                                            {v.is_completed ? (
                                                <Chip
                                                    size="sm" variant="soft" color="success"
                                                    sx={{ fontSize: '0.66rem' }}
                                                    title={v.completion_type === 'auto'
                                                        ? 'Досмотрел последний слайд до конца таймера'
                                                        : 'Нажал «Ок»'}
                                                >
                                                    {v.completion_type === 'auto' ? '⏱ ' : '✓ '}
                                                    {fmtDateTime(v.completed_at)}
                                                </Chip>
                                            ) : v.viewed_all ? (
                                                // Видел все слайды, но не подтвердил и не досидел таймер:
                                                // новость покажется ему ещё раз
                                                <Chip size="sm" variant="soft" color="primary" sx={{ fontSize: '0.66rem' }}>
                                                    Дошёл до конца
                                                </Chip>
                                            ) : (
                                                <Chip size="sm" variant="soft" color="warning" sx={{ fontSize: '0.66rem' }}>
                                                    Не досмотрел
                                                </Chip>
                                            )}
                                        </td>
                                        <td>{v.slides_seen} / {v.slides_total ?? viewers.slides_count}</td>
                                        <td>{fmtSec(v.total_duration_sec)}</td>
                                        <td>{v.open_count}</td>
                                        <td>{v.device === 'mobile' ? 'Телефон' : v.device === 'desktop' ? 'ПК' : '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Box>
                )}

                {viewers.num_pages > 1 && (
                    <Stack direction="row" spacing={1} justifyContent="center" sx={{ p: 2 }}>
                        <Button size="sm" variant="soft" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Назад</Button>
                        <Button size="sm" variant="plain" disabled>{page} / {viewers.num_pages}</Button>
                        <Button size="sm" variant="soft" disabled={page >= viewers.num_pages} onClick={() => setPage(p => p + 1)}>Вперёд</Button>
                    </Stack>
                )}
            </Sheet>

            {/* Не увидели */}
            <Sheet sx={cardSx}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <PersonOffRoundedIcon sx={{ fontSize: 19, color: 'text.tertiary' }} />
                        <Typography sx={{ fontWeight: 800, fontSize: '0.95rem' }}>Ещё не открыли</Typography>
                        {notViewed && (
                            <Chip size="sm" variant="soft" color="warning">{notViewed.not_opened_total}</Chip>
                        )}
                    </Stack>
                    <Button size="sm" variant="soft" onClick={loadNotViewed}>
                        {showNotViewed ? 'Свернуть' : 'Показать'}
                    </Button>
                </Stack>

                {showNotViewed && notViewed && (
                    <Box
                        component={motion.div}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        sx={{ mt: 1.8 }}
                    >
                        <Stack direction="row" spacing={2} sx={{ mb: 1.6, flexWrap: 'wrap' }}>
                            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                Аудитория: <b>{notViewed.audience_total}</b>
                            </Typography>
                            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                Открыли: <b>{notViewed.opened_total}</b>
                            </Typography>
                            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                Начали, но не завершили: <b>{notViewed.started_not_finished_total}</b>
                            </Typography>
                        </Stack>

                        <Stack direction="row" spacing={0.8} sx={{ flexWrap: 'wrap', gap: 0.8 }}>
                            {(notViewed.results || []).map(emp => (
                                <Chip key={emp.employee_id} variant="soft" color="neutral" sx={{ fontSize: '0.74rem' }}>
                                    {emp.full_name}{emp.city ? ` · ${emp.city}` : ''}
                                </Chip>
                            ))}
                            {(notViewed.results || []).length === 0 && (
                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                    Все из целевой аудитории уже открывали новость
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                )}
            </Sheet>
        </Box>
    );
}